import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	KlausurEditingProvider,
	useKlausurEditing,
} from "@/features/klausur/context/klausurEditingProvider";
import KlausurErstellerSidebar from "@/features/klausur/components/klausurErstellerSidebar.component";
import type { NavigationKlausurDTO } from "@/types/klausurEditor.types";
import { KlausurSidebarSelectionProvider } from "@/features/klausur/context/klausurSidebarSelectionContext";
import { useLayout } from "@/lib/contexts/layout.context";
import { KlausurErstellerTopBar } from "../components/klausurErstellerTopBar.component";
import KlausurEditorContent from "../components/klausurEditorContent.component";
import { KlausurEditorScrollProvider } from "@/features/klausur/context/klausurEditorScrollProvider";
import { OrderingModalProvider } from "@/features/klausur/context/orderingModalProvider";
import { KlausurErstellerSidebarActionsProvider } from "@/features/klausur/context/klausurErstellerSidebarActionsProvider";

/**
 * Inner component for the KlausurEditorPage
 */

function KlausurEditorInner() {
	const { klausurId, klausurMeta, navigationKlausur, loadKlausurMeta } =
		useKlausurEditing();
	const navigate = useNavigate();
	const sidebarKlausur: NavigationKlausurDTO = useMemo(
		() =>
			navigationKlausur ?? {
				id: klausurId,
				title: klausurMeta?.title,
				aufgabenstellungen: [],
			},
		[navigationKlausur, klausurId, klausurMeta?.title],
	);

	useEffect(() => {
		loadKlausurMeta(klausurId);
	}, [klausurId, loadKlausurMeta]);

	return (
		<KlausurSidebarSelectionProvider initialKlausur={sidebarKlausur}>
			<OrderingModalProvider>
				<KlausurErstellerSidebarActionsProvider>
					<KlausurErstellerTopBar
						klausurTitle={klausurMeta?.title ?? ""}
						handleBackToDashboard={() => navigate(-1)}
					/>
					<KlausurEditorScrollProvider>
						<div className="flex w-full h-[var(--height-main-content)] overflow-hidden">
							<KlausurErstellerSidebar klausur={sidebarKlausur} />
							<div className="w-full h-full space-y-6 overflow-hidden">
								{navigationKlausur?.aufgabenstellungen.length === 0 ? (
									<div className="text-muted-foreground italic">
										Keine Aufgabenstellungen vorhanden, es können neue über
										"Neue Aufgabenstellung" erstellt werden.
									</div>
								) : (
									<KlausurEditorContent />
								)}
							</div>
						</div>
					</KlausurEditorScrollProvider>
				</KlausurErstellerSidebarActionsProvider>
			</OrderingModalProvider>
		</KlausurSidebarSelectionProvider>
	);
}

/**
 * Page for the KlausurEditor
 * This also holds the provider for the KlausurEditingContext
 */
export default function KlausurEditorPage() {
	const { klausurId = "" } = useParams();
	const { setShowNavbar } = useLayout();

	useEffect(() => {
		setShowNavbar(false);
		return () => setShowNavbar(true);
	}, [setShowNavbar]);

	if (!klausurId) {
		return <div>Ein Fehler ist aufgetreten</div>;
	}

	return (
		<KlausurEditingProvider klausurId={klausurId}>
			<KlausurEditorInner />
		</KlausurEditingProvider>
	);
}
